import { useSentryQuery, useSentryMutation } from '../utils/sentryQueryWrapper';
import {
  PickupResponse,
  PickUpParams,
  PickupDataResponse,
  ErrorMessageResponse,
  PickUpSingleResponse,
  DownloadPickupLabel,
  PreferredTiming,
  ResponseType,
} from '@/types';
import {
  bulkCancelPickup,
  bulkRequestPickup,
  cancelPickupById,
  fetchPickupLabel,
  fetchPickupOrders,
  fetchPickups,
  fetchRTO,
  fetchRtoOrders,
  fetchSinglePickupById,
  fetchSingleRTOById,
  generatePickupLabel,
  requestPickup,
} from '@/api';
import { t } from 'i18next';
import useLanguageStore from '@/stores/useLanguageStore';

// Fetch Pickups
//
// v2-task-12fix (M1): `slot.label` on each row (PickUp::getSlotAttribute) is
// localized SERVER-SIDE from the request's `lang` param — the exact same
// mechanism `useFetchPickupSlots` relies on (see usePickupSlots.ts's
// docblock for the full v2-task-8b writeup). That hook's query key was
// already scoped by `language` to fix a stale-locale-label bug; this query
// key was missed, so switching the app language did NOT invalidate an
// already-cached pickups-list page, and the "Pickup Time Slot" column kept
// showing the label from whatever locale was active at the last fetch
// (reported live as English "Evening" inside an otherwise-Arabic row).
// Scoping this key the same way makes a language switch a fresh fetch, so
// the table cell renders the same server-localized label the modal gets.

// How often the Pickups list re-reads itself while the merchant is looking at
// it. The rollover pop-up is NOT pushed — `useRolloverToastSync` derives it
// from this very list (the newest row with `slot_response_open`), so the list
// refetching IS the delivery mechanism. Without an interval the merchant saw
// the pop-up only after a manual reload: `refetchOnMount` is true (so leaving
// and coming back worked) but `refetchOnWindowFocus` is false and nothing
// polled, so sitting on the page showed nothing, forever — while a 30-minute
// response window ran down and push delivery was still pending.
//
// A minute is far inside that window and cheap: `enabled` here means "the
// Pickups tab is active" and React Query does not poll a disabled query, and
// the global `refetchIntervalInBackground: false` stops it in a hidden tab. So
// the cost is one lightweight request per minute per merchant actually
// watching the page — not one per logged-in merchant.
export const PICKUPS_POLL_INTERVAL_MS = 60_000;

export const useFetchPickups = (
  page: number,
  pageSize?: number,
  enabled = true,
  search?: string,
) => {
  const language = useLanguageStore((state) => state.language);
  return useSentryQuery<PickupResponse, ErrorMessageResponse>(
    ['pickups', page, pageSize, search, language],
    () => fetchPickups(page, pageSize, search),
    {
      keepPreviousData: true,
      enabled,
      refetchInterval: PICKUPS_POLL_INTERVAL_MS,
    },
  );
};

// Fetch RTO (Returns)
export const useFetchRTO = (page: number, pageSize?: number, enabled = true, search?: string) => {
  return useSentryQuery<PickupResponse, ErrorMessageResponse>(
    ['return_to_origin', page, pageSize, search],
    () => fetchRTO(page, pageSize, search),
    {
      keepPreviousData: true,
      enabled,
    },
  );
};

// Fetch Single Pickup By ID
export const useFetchSinglePickupById = (uid: string, refetchInterval?: number, enabled = true) => {
  return useSentryQuery<PickUpSingleResponse, ErrorMessageResponse>(
    ['pickup', uid],
    () => fetchSinglePickupById(uid),
    {
      enabled,
      refetchInterval,
      refetchIntervalInBackground: !!refetchInterval,
    },
  );
};
// Fetch Single RTO By ID
export const useFetchSingleRTOById = (uid: string, refetchInterval?: number, enabled = true) => {
  return useSentryQuery<PickUpSingleResponse, ErrorMessageResponse>(
    ['return_to_origin', uid],
    () => fetchSingleRTOById(uid),
    {
      enabled,
      refetchInterval,
      refetchIntervalInBackground: !!refetchInterval,
    },
  );
};

// Request Pickup
export const useRequestPickup = () => {
  return useSentryMutation<PickupDataResponse, ErrorMessageResponse, PickUpParams>(
    (data) => requestPickup(data),
    { defaultErrorMessage: t('failed_pickup_request') },
  );
};
// Bulk Request Pickup
export const useBulkRequestPickup = () => {
  return useSentryMutation<
    void,
    ErrorMessageResponse,
    { selectedIds: string[]; timeSlotCode?: string; preferredTiming?: PreferredTiming | null }
  >(
    ({ selectedIds, timeSlotCode, preferredTiming }) =>
      bulkRequestPickup(selectedIds, timeSlotCode, preferredTiming),
    {
      defaultErrorMessage: t('failed_pickup_request'),
    },
  );
};

// Bulk Cancel Pickup
export const useBulkCancelPickup = () => {
  return useSentryMutation<void, ErrorMessageResponse, string[]>((selectedIds) =>
    bulkCancelPickup(selectedIds),
  );
};

// Cancel Pickup By ID
export const useCancelPickupById = () => {
  return useSentryMutation<PickupResponse, ErrorMessageResponse, string>((id: string) =>
    cancelPickupById(id),
  );
};

// Fetch Pickup Label
export const useGeneratePickupLabel = () => {
  return useSentryMutation<void, ErrorMessageResponse, string>((id: string) =>
    generatePickupLabel(id),
  );
};
// Fetch Pickup Label
export const useFetchPickupLabel = () => {
  return useSentryMutation<ResponseType<DownloadPickupLabel>, ErrorMessageResponse, string>(
    (id: string) => fetchPickupLabel(id),
  );
};

// Fetch orders in a pickup
export const useFetchPickupOrders = (pickupUid: string, enabled: boolean = true) => {
  return useSentryQuery<any, ErrorMessageResponse>(
    ['pickup-orders', pickupUid],
    () => fetchPickupOrders(pickupUid),
    { enabled: enabled && !!pickupUid },
  );
};

// Fetch orders in an RTO
export const useFetchRtoOrders = (rtoUid: string, enabled: boolean = true) => {
  return useSentryQuery<any, ErrorMessageResponse>(
    ['rto-orders', rtoUid],
    () => fetchRtoOrders(rtoUid),
    { enabled: enabled && !!rtoUid },
  );
};
